import type { AxiosError } from 'axios';
import { Button, Stack, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import type { OfferLine } from '@/api/generated/models';
import {
  getGetOfferQueryKey,
  getListOfferLinesQueryKey,
  useAddOfferLine,
  useUpdateOfferLine
} from '@/api/generated/offers/offers';
import { useListUnits } from '@/api/generated/units/units';
import {
  CreatableSelectField,
  FormModal,
  ModalFooter,
  MutationButton,
  NumberField,
  SelectField,
  TextField,
  Toast
} from '@/components/common';
import { activeUnitOptions } from '@/features/units/unitDisplay';

const schema = z.object({
  itemCode: z.string().min(1),
  category: z.string().min(1),
  unitId: z.string().min(1),
  description: z.string().min(1),
  // 0 dopušteno — vidi OfferService.assertValidQuantity/AddContractLineRequest (isti obrazac kao ContractLine).
  quantity: z.number().int().min(0),
  unitPrice: z.number().positive(),
  normHours: z.number().nullable()
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = {
  itemCode: '',
  category: '',
  unitId: '',
  description: '',
  quantity: 0,
  unitPrice: 0,
  normHours: null
};

// Isti obrazac kao PriceListItemFormModal — backend već vraća specifičan,
// lokalizirani razlog ako nešto ne uspije.
const lineErrorMessage = (error: unknown, fallback: string): string => {
  const backendMessage = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
  return typeof backendMessage === 'string' ? backendMessage : fallback;
};

interface OfferLineFormModalProps {
  opened: boolean;
  onClose: () => void;
  offerId: string;
  existingCategories?: string[];
  /** Kad je zadan, modal uređuje ovu (ad-hoc) stavku umjesto dodavanja nove. */
  editingLine?: OfferLine | null;
  onSaved?: (message: string) => void;
}

/**
 * Ručni unos/uređivanje stavke — koristi se kad ponuda NIJE vezana na cjenik
 * (offer.priceListId prazan), pa se stavke ne mogu birati iz cjenika
 * (OfferItemPickerModal). Backend (AddOfferLineRequest/OfferService.addLine,
 * UpdateOfferLineRequest/OfferService.updateLine) već podržava oba puta bez
 * priceListItemId — uređivanje itemCode/category/description/unit/normHours
 * je backend namjerno zabranio SAMO na stavkama preuzetim iz cjenika.
 */
export const OfferLineFormModal = ({
  opened,
  onClose,
  offerId,
  existingCategories = [],
  editingLine,
  onSaved
}: OfferLineFormModalProps) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = !!editingLine;

  const { data: unitsData } = useListUnits();
  const unitOptions = activeUnitOptions(unitsData, i18n.language, editingLine?.unitId ?? undefined);

  const form = useForm<FormValues>({
    validate: zodResolver(schema),
    initialValues: emptyValues
  });

  useEffect(() => {
    if (!opened) return;
    form.setValues(
      editingLine
        ? {
            itemCode: editingLine.itemCode ?? '',
            category: editingLine.category ?? '',
            unitId: editingLine.unitId ?? '',
            description: editingLine.description ?? '',
            quantity: editingLine.quantity ?? 1,
            unitPrice: editingLine.unitPrice ?? 0,
            normHours: editingLine.normHours ?? null
          }
        : emptyValues
    );
  }, [opened, editingLine]);

  const invalidateLines = () => {
    queryClient.invalidateQueries({ queryKey: getListOfferLinesQueryKey(offerId) });
    queryClient.invalidateQueries({ queryKey: getGetOfferQueryKey(offerId) });
  };

  const addMutation = useAddOfferLine({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        invalidateLines();
        onSaved?.(t('offerLineForm.addSuccess'));
        onClose();
      }
    }
  });

  const updateMutation = useUpdateOfferLine({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        invalidateLines();
        onSaved?.(t('offerLineForm.editSuccess'));
        onClose();
      }
    }
  });

  const mutation = isEdit ? updateMutation : addMutation;

  const handleSubmit = (values: FormValues) => {
    if (editingLine?.id) {
      updateMutation.mutate({
        id: offerId,
        lineId: editingLine.id,
        data: {
          unitPrice: values.unitPrice,
          quantity: values.quantity,
          itemCode: values.itemCode,
          category: values.category,
          description: values.description,
          unitId: values.unitId,
          normHours: values.normHours ?? undefined
        }
      });
    } else {
      addMutation.mutate({
        id: offerId,
        data: {
          itemCode: values.itemCode,
          category: values.category,
          description: values.description,
          unitId: values.unitId,
          quantity: values.quantity,
          unitPrice: values.unitPrice,
          normHours: values.normHours ?? undefined
        }
      });
    }
  };

  return (
    <FormModal
      opened={opened}
      onClose={onClose}
      title={
        <Stack gap={4}>
          <Text fz={20} fw={700} style={{ color: 'var(--color-text-primary)' }}>
            {t(isEdit ? 'offerLineForm.editTitle' : 'offerLineForm.title')}
          </Text>
          <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
            {t(isEdit ? 'offerLineForm.editIntro' : 'offerLineForm.intro')}
          </Text>
        </Stack>
      }
      styles={{
        content: { backgroundColor: 'var(--color-bg-surface)' },
        header: {
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-soft)'
        },
        body: { padding: 20 }
      }}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap={16}>
          {mutation.isError && (
            <Toast tone="danger">{lineErrorMessage(mutation.error, t('common.error'))}</Toast>
          )}

          <TextField
            label={t('offerLineForm.fieldItemCode')}
            required
            {...form.getInputProps('itemCode')}
          />

          <div className="form-row-2col">
            <div style={{ flex: 1 }}>
              <CreatableSelectField
                label={t('offerLineForm.fieldCategory')}
                required
                placeholder={t('offerLineForm.categoryPlaceholder')}
                data={existingCategories}
                createLabel={(query) => t('offerLineForm.categoryCreate', { value: query })}
                emptyLabel={t('offerLineForm.categoryEmpty')}
                {...form.getInputProps('category')}
              />
            </div>
            <div style={{ flex: 1 }}>
              <SelectField
                label={t('offerLineForm.fieldUnit')}
                required
                allowDeselect={false}
                placeholder={t('priceLists.unitPlaceholder')}
                data={unitOptions}
                {...form.getInputProps('unitId')}
              />
            </div>
          </div>

          <TextField
            label={t('offerLineForm.fieldDescription')}
            required
            {...form.getInputProps('description')}
          />

          <div className="form-row-2col">
            <div style={{ flex: 1 }}>
              <NumberField
                label={t('offerLineForm.fieldQuantity')}
                required
                allowNegative={false}
                allowDecimal={false}
                min={1}
                {...form.getInputProps('quantity')}
              />
            </div>
            <div style={{ flex: 1 }}>
              <NumberField
                label={t('offerLineForm.fieldUnitPrice')}
                required
                allowNegative={false}
                decimalScale={2}
                fixedDecimalScale
                {...form.getInputProps('unitPrice')}
              />
            </div>
          </div>

          <NumberField
            label={t('offerLineForm.fieldNormHours')}
            allowNegative={false}
            decimalScale={2}
            {...form.getInputProps('normHours')}
          />

          <ModalFooter>
            <Button variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <MutationButton
              type="submit"
              isPending={mutation.isPending}
              isPaused={mutation.isPaused}
            >
              {t('offerLineForm.saveButton')}
            </MutationButton>
          </ModalFooter>
        </Stack>
      </form>
    </FormModal>
  );
};
